import { ColorAnimatedTextureChunkedShader } from "./ColorAnimatedTextureChunkedShader";
import { ShaderInputs } from "./ShaderInputs";
import { FOG_CHUNK_FS, FOG_CHUNK_VS, FOG_UNIFORMS_FS, FOG_UNIFORMS_VS } from "./FogChunks";

/**
 * Puts [addition] right after [marker] of the shader code of the base class. The marker is
 * checked, so moving the code it belongs to fails loudly instead of silently dropping the fog.
 */
function after(code: string, marker: string, addition: string, shader: string): string {
    if (!code.includes(marker)) {
        throw new Error(`Cannot add the fog to ${shader}: '${marker}' is not in it`);
    }

    return code.replace(marker, `${marker}\n${addition}`);
}

/**
 * [ColorAnimatedTextureChunkedShader] of the bats with the linear fog of the scene mixed in,
 * so they fade into the distance like the rest of it.
 *
 * The animation itself is the one of the base shader: the positions come from the texture and
 * the fog is only added to the code it builds.
 *
 * Uniforms are the ones of the base shader followed by the fog: [view_proj_matrix, color,
 * uTextureWidthInt, uTexelHeight, sPositions, uTexelSizes, fogColor, fogStartDistance,
 * fogDistance] - the order the bat draw commands put them into.
 */
export class ColorAnimatedTextureChunkedFogShader extends ColorAnimatedTextureChunkedShader implements ShaderInputs {
    fogColor: WebGLUniformLocation | undefined;
    fogStartDistance: WebGLUniformLocation | undefined;
    fogDistance: WebGLUniformLocation | undefined;

    /** @inheritdoc */
    fillCode() {
        super.fillCode();

        this.vertexShaderCode = after(
            after(this.vertexShaderCode, `uniform mat4 view_proj_matrix;`, FOG_UNIFORMS_VS, "the vertex shader"),
            `gl_Position = view_proj_matrix * position;`, FOG_CHUNK_VS, "the vertex shader"
        );

        this.fragmentShaderCode = after(
            after(this.fragmentShaderCode, `out vec4 fragColor;`, FOG_UNIFORMS_FS, "the fragment shader"),
            `fragColor = color;`, FOG_CHUNK_FS, "the fragment shader"
        );
    }

    /** @inheritdoc */
    fillUniformsAttributes() {
        super.fillUniformsAttributes();

        this.fogColor = this.getUniform("fogColor");
        this.fogStartDistance = this.getUniform("fogStartDistance");
        this.fogDistance = this.getUniform("fogDistance");

        // The fog is added after the uniforms of the base shader, so the positions the bat
        // commands and the animation of the scene use stay where they are.
        this.uniforms = this.uniforms!.concat([
            this.fogColor,
            this.fogStartDistance,
            this.fogDistance
        ]);
    }
}
